import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { getQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { useSelector } from 'react-redux';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useAppSelector } from '../../reducers';
import { productsAndPacksForQueryParamsSelector } from '../../selectors/ProductConfigsSelectors';
import { productVersionFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { currentSkuIdForProductSelector, productIdForQuantityPackAndProductApiNameSelector, productVersionSelector } from '../../selectors/ProductsSelectors';
import { productDataSelector } from '../../selectors/SelectorUtils';
import { useQueryParamsEffect } from '../urls/appQueryParamUtils';

// If a customer owns a legacy product, without this check it's possible for
// planBuilder to add both the legacy sku and the current sku to
// productConfigurations, so the product shows up twice (e.g. a bundle sku)
export const currentOrOwnedSkuIdSelector = (state, {
  productAttributeFilters
}) => {
  const ownedProductVersion = productVersionFromProductInventorySelector(state, {
    productAttributeFilters
  });
  const ownedSkuId = ownedProductVersion && ownedProductVersion.skuId;
  const currentSkuId = currentSkuIdForProductSelector(state, productAttributeFilters);
  return ownedSkuId || currentSkuId;
};
export const queryParamProductConfigurationsSelector = (state, {
  queryParams
}) => {
  const productConfigurations = [];
  const {
    products,
    packs
  } = queryParams;
  const {
    apiNameToProductId
  } = productDataSelector(state);
  const stringProductConfigs = products ? typeof products === 'string' ? [products] : products : [];
  const stringQuantityPacks = packs ? typeof packs === 'string' ? [packs] : packs : [];
  const validatedQuantity = quantityString => {
    if (!quantityString) return 1;
    const quantity = Math.abs(Number.parseInt(quantityString, 10));
    return Number.isInteger(quantity) ? quantity : 1;
  };
  stringProductConfigs.forEach(entry => {
    const split = entry.toString().split('_');
    if (split.length > 0) {
      const apiName = split[0];
      const quantity = validatedQuantity(split[1]);
      if (apiNameToProductId && apiName in apiNameToProductId) {
        productConfigurations.push(createProductConfig({
          skuId: currentOrOwnedSkuIdSelector(state, {
            productAttributeFilters: {
              apiName
            }
          }),
          quantity
        }));
      }
    }
  });
  stringQuantityPacks.forEach(entry => {
    const split = entry.toString().split('_');
    if (split.length === 3) {
      // if url uses quantityPackApiName. E.g. ?packs=additional-sales-seats_sales-hub-starter_2
      const [quantityPackApiName, productApiName, quantityString] = split;
      const quantity = validatedQuantity(quantityString);
      const productId = productIdForQuantityPackAndProductApiNameSelector(state, {
        quantityPackApiName: quantityPackApiName,
        productApiName: productApiName
      });
      if (quantity >= 0 && productId && quantityPackApiName) {
        const parentSkuId = currentOrOwnedSkuIdSelector(state, {
          productAttributeFilters: {
            productId
          }
        });
        const {
          quantityPackDetails
        } = productVersionSelector(state, {
          skuId: parentSkuId
        });
        const quantityPack = quantityPackDetails.find(({
          quantityPackApiName: searchQuantityPackApiName
        }) => quantityPackApiName === searchQuantityPackApiName);
        if (quantityPack) {
          const {
            skuId,
            includedWithQuantityPack
          } = quantityPack;

          // if quantity is a small number (e.g. 2), multiply by includedWithQuantityPack (e.g. 1000) to convert to displayed units (e.g. 2000)
          // this handles cases where other apps link with small units instead of displayed units
          const isSmallUnitQuantity = quantity < includedWithQuantityPack; // threshold for determining if using small units
          const adjustedQuantity = isSmallUnitQuantity ? quantity * includedWithQuantityPack : quantity;
          productConfigurations.push({
            skuId,
            quantity: adjustedQuantity
          });
        }
      }
    }
  });
  return productConfigurations;
};
export const useQueryParamProductConfigsOrDefault = ({
  defaultConfigurations
}) => {
  const queryParams = getQueryParams();
  const isAppReady = useGlobalLoading();
  const queryParamProductConfigurations = useAppSelector(state => isAppReady ? queryParamProductConfigurationsSelector(state, {
    queryParams
  }) : []);
  if (queryParamProductConfigurations.length) {
    return queryParamProductConfigurations;
  }
  return defaultConfigurations;
};
export const useProductConfigsQueryParamsParamsEffect = productConfigurations => {
  const {
    products,
    packs
  } = useSelector(state => productsAndPacksForQueryParamsSelector(state, {
    productConfigurations
  }));
  useQueryParamsEffect({
    products,
    packs
  });
};